import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, cn, DEPARTMENT_LABELS, DEPARTMENT_COLORS } from "../lib/utils";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw, AlertTriangle, Clock, FolderOpen, ClipboardList,
  ChevronRight, Factory, Filter,
} from "lucide-react";

export default function TasksPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchSummary();
  }, [statusFilter]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      let url = `${API}/tasks/all-tasks-summary`;
      if (statusFilter && statusFilter !== "all") {
        url += `?status=${statusFilter}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      setSummary(response.data);
    } catch (err) {
      console.error("Failed to fetch task summary:", err);
      toast.error("Failed to load task summary");
    } finally {
      setLoading(false);
    }
  };

  const hasTasks = Object.keys(summary).length > 0;
  const deptOrder = ["merchandizing", "prepress", "production", "logistics", "packaging"];

  if (loading) {
    return <SummarySkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">All Tasks</h1>
          <p className="text-muted-foreground mt-1">
            Summarized view of all tasks across departments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] rounded-sm h-9" data-testid="all-tasks-status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchSummary} className="rounded-sm" data-testid="refresh-all-tasks-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {!hasTasks ? (
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="py-16 text-center text-muted-foreground">
            <Factory className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="font-medium">No tasks found</p>
            <p className="text-sm mt-1">
              {statusFilter !== "all" ? "No tasks match the selected filter" : "Tasks will appear when orders are approved"}
            </p>
          </CardContent>
        </Card>
      ) : (
        deptOrder.map((dept) => {
          const taskTypes = summary[dept];
          if (!taskTypes || taskTypes.length === 0) return null;
          const colors = DEPARTMENT_COLORS[dept] || DEPARTMENT_COLORS.merchandizing;
          return (
            <DepartmentSection
              key={dept}
              dept={dept}
              taskTypes={taskTypes}
              colors={colors}
              navigate={navigate}
            />
          );
        })
      )}
    </div>
  );
}

function DepartmentSection({ dept, taskTypes, colors, navigate }) {
  return (
    <div className="space-y-3" data-testid={`dept-section-${dept}`}>
      <h2 className={cn("font-heading text-xl tracking-tight capitalize", colors.text)}>
        {DEPARTMENT_LABELS[dept] || dept}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {taskTypes.map((tt) => (
          <TaskTypeCard
            key={tt.task_name}
            data={tt}
            onClick={() =>
              navigate(`/tasks/detail?department=${dept}&task_name=${encodeURIComponent(tt.task_name)}`)
            }
          />
        ))}
      </div>
    </div>
  );
}

function TaskTypeCard({ data, onClick }) {
  const hasOverdue = data.overdue > 0;
  const hasPending = data.pending > 0;

  return (
    <Card
      className={cn(
        "border shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md hover:border-slate-300",
        hasOverdue && "border-red-300 bg-red-50/30"
      )}
      onClick={onClick}
      data-testid={`task-type-card-${data.task_name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-semibold text-sm leading-tight">{data.task_name}</h3>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
        </div>
        <ul className="space-y-1.5 text-sm">
          <li className={cn("flex items-center gap-2", hasOverdue ? "text-red-600 font-medium" : "text-muted-foreground")}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{data.overdue} task overdue</span>
          </li>
          <li className={cn("flex items-center gap-2", hasPending ? "text-amber-600" : "text-muted-foreground")}>
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{data.pending} task pending</span>
          </li>
          <li className="flex items-center gap-2 text-muted-foreground">
            <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{data.orders_open} orders open</span>
          </li>
          <li className="flex items-center gap-2 text-muted-foreground">
            <ClipboardList className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{data.orders_total} orders</span>
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}

const SummarySkeleton = () => (
  <div className="space-y-6">
    <div>
      <Skeleton className="h-9 w-48" />
      <Skeleton className="h-4 w-64 mt-2" />
    </div>
    {[1, 2, 3].map((i) => (
      <div key={i} className="space-y-3">
        <Skeleton className="h-7 w-40" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((j) => (
            <Skeleton key={j} className="h-36" />
          ))}
        </div>
      </div>
    ))}
  </div>
);
